/**
 * web/src/games/GuessmatePanel.tsx — Guessmate, the five-letter duel
 * (card 8gbGPJVD3ch4, slice 5: the Games-tab panel).
 *
 * The panel renders state and never decides anything: whose turn it is, how
 * many guesses are left, what a guess scored and who won are all the server's
 * answers, re-read after every action. The partner's secret word is absent
 * from every pre-settle payload by construction, so this file cannot leak it
 * even by mistake.
 *
 * Live mode has two minutes per turn. Rather than open a second socket
 * listener beside Nocturne's, the panel re-reads on a bounded interval while
 * a match is in play — one GET every eight seconds, the same read the initial
 * load uses, and it stops the moment the match settles.
 */
import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import type { GuessmateGuess, GuessmateMatch, GuessmateMode } from '../../../shared/types';
import { api, session } from '../shell/bridge';
import { announcePlusRequired } from '../lib/billing';
import { Pressable } from '../ui/Pressable';
import './guessmate.css';

const BASE = '/api/game/guessmate';
const POLL_MS = 8_000;

interface Member {
  memberId: string;
  displayName: string;
}

/** The wire throws `Error(JSON.stringify({error:{code,message}}))` — the panel
 *  reacts to the code, never to the prose. */
function errorCode(e: unknown): string | null {
  if (!(e instanceof Error)) return null;
  try {
    return (JSON.parse(e.message) as { error?: { code?: string } })?.error?.code ?? null;
  } catch {
    return null;
  }
}

/** Every refusal the duel routes can answer, in the words a person needs.
 *  `word_list_unavailable` is honest rather than clever: the curated five-letter
 *  list is not switched on yet, so the panel says so instead of offering a
 *  retry that can never work. */
const ERROR_COPY: Record<string, string> = {
  word_not_accepted: 'Word not accepted, try another.',
  word_list_unavailable: 'Guessmate is nearly ready — the word list is not switched on yet.',
  word_check_failed: 'Could not check that word just now — try again.',
  already_set: 'Your word is already locked in.',
  no_guesses_left: 'That is all eight of your guesses.',
  not_your_turn: 'It is the other one of you to go.',
  not_a_player: 'Only the two of you can play this match.',
  not_paired: 'Guessmate needs both of you in the room.',
  not_open: 'That match has moved on — reload to catch up.',
  plus_required: 'Guessmate is part of Pear Plus.',
};

function Board({ guesses, label }: { guesses: GuessmateGuess[]; label: string }) {
  if (guesses.length === 0) {
    return <p className="game-hint gm-board-empty">{label}: nothing yet.</p>;
  }
  return (
    <div className="gm-board" role="list" aria-label={label}>
      {guesses.map(g => (
        <div className="gm-row" role="listitem" key={`${g.index}-${g.word}`}>
          {g.word.split('').map((letter, i) => (
            <span
              key={`${i}-${letter}`}
              className={`gm-tile gm-${g.verdicts[i] ?? 'grey'}`}
              aria-label={`${letter} ${g.verdicts[i] ?? 'grey'}`}
            >
              {letter.toUpperCase()}
            </span>
          ))}
          <span className="gm-score" aria-label={`${g.greens} greens`}>
            {g.greens}
          </span>
        </div>
      ))}
    </div>
  );
}

export function GuessmatePanel({ members }: { members: Member[] }) {
  const me = session();
  const [match, setMatch] = useState<GuessmateMatch | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [gated, setGated] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<GuessmateMode>('async');
  const [wordDraft, setWordDraft] = useState('');
  const [guessDraft, setGuessDraft] = useState('');
  const [clock, setClock] = useState(() => Date.now());
  /* Overlapping reads (initial, action, poll) can land out of order; every
     reload owns a generation and a stale response is discarded. */
  const gen = useRef(0);

  const reload = useCallback(async () => {
    const mine = ++gen.current;
    try {
      const data = await api<{ match: GuessmateMatch | null }>(`${BASE}/match`);
      if (mine !== gen.current) return;
      setMatch(data.match ?? null);
      setLoadError(false);
      setGated(false);
    } catch (e) {
      if (e instanceof DOMException && e.name === 'AbortError') return;
      const code = errorCode(e);
      if (code === 'plus_required') {
        if (mine !== gen.current) return;
        setGated(true);
        setLoadError(false);
        announcePlusRequired();
      } else if (mine === gen.current) {
        console.error('[Pear] guessmate load failed', e);
        setLoadError(true);
      }
    } finally {
      if (mine === gen.current) setLoaded(true);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  /* The clock the countdown reads — only while a turn is actually running. */
  useEffect(() => {
    if (!match || match.state !== 'playing' || !match.myTurn) return;
    const id = window.setInterval(() => setClock(Date.now()), 1_000);
    return () => window.clearInterval(id);
  }, [match]);

  /* Bounded re-read while a match is in play, so the partner's move appears
     without either side pressing anything. Stops on settle. */
  useEffect(() => {
    if (!match || match.state !== 'playing') return;
    const id = window.setInterval(() => void reload(), POLL_MS);
    return () => window.clearInterval(id);
  }, [match?.matchId, match?.state, reload]);

  const act = async (call: () => Promise<{ match: GuessmateMatch }>): Promise<boolean> => {
    setBusy(true);
    try {
      const data = await call();
      setMatch(data.match);
      setError(null);
      return true;
    } catch (e) {
      const code = errorCode(e);
      setError(
        (code && ERROR_COPY[code]) || 'That did not save — try again.',
      );
      if (code === 'plus_required') announcePlusRequired();
      return false;
    } finally {
      setBusy(false);
    }
  };

  const start = (chosen: GuessmateMode) =>
    act(() => api<{ match: GuessmateMatch }>(`${BASE}/start`, { body: { mode: chosen } }));

  const setWord = async (e: FormEvent) => {
    e.preventDefault();
    if (!match || busy) return;
    const word = wordDraft.trim().toLowerCase();
    if (word.length !== 5) {
      setError('A word is exactly five letters.');
      return;
    }
    if (await act(() => api<{ match: GuessmateMatch }>(`${BASE}/word`, { body: { matchId: match.matchId, word } }))) {
      setWordDraft('');
    }
  };

  const guess = async (e: FormEvent) => {
    e.preventDefault();
    if (!match || busy) return;
    const word = guessDraft.trim().toLowerCase();
    if (word.length !== 5) {
      setError('A guess is exactly five letters.');
      return;
    }
    if (await act(() => api<{ match: GuessmateMatch }>(`${BASE}/guess`, { body: { matchId: match.matchId, word } }))) {
      setGuessDraft('');
    }
  };

  const mine = members.find(m => m.memberId === me?.memberId);
  const partner = members.find(m => m.memberId !== me?.memberId);
  const myName = mine?.displayName ?? 'You';
  const partnerName = partner?.displayName ?? 'your partner';

  const secondsLeft =
    match?.myTurn && match.deadlineAt ? Math.max(0, Math.ceil((match.deadlineAt - clock) / 1000)) : null;
  /* 120 s turns read as 2:00, not as "120s": the clock is minutes once the
     live turn went past a minute (founder email 2026-11-04). */
  const clockLine =
    secondsLeft == null ? null : `${Math.floor(secondsLeft / 60)}:${String(secondsLeft % 60).padStart(2, '0')}`;

  /* A five-green row is a solve, and a solve is a win on the spot — say that
     rather than the greens total, which is only the tie-break finish. */
  const solved = (guesses: GuessmateMatch['myGuesses']): boolean =>
    guesses.some(g => g.greens === 5);

  const outcomeLine = (m: GuessmateMatch): string => {
    if (m.outcome === 'draw') return 'A draw — level on greens.';
    const mine = (m.outcome === 'seat0' && m.mySeat === 0) || (m.outcome === 'seat1' && m.mySeat === 1);
    if (mine) return solved(m.myGuesses) ? `${myName} got the word first.` : `${myName} wins on greens.`;
    return solved(m.partnerGuesses) ? `${partnerName} got the word first.` : `${partnerName} wins on greens.`;
  };

  return (
    <section className="game-panel guessmate" id="guessmate-panel" aria-labelledby="guessmate-title">
      <h2 className="game-title" id="guessmate-title">
        Guessmate
      </h2>
      <p className="game-hint" id="guessmate-hint">
        You each pick a secret five-letter word. Take turns guessing theirs — green is the right
        letter in the right place, amber is the right letter elsewhere. Eight guesses each: solve
        their word first and you win on the spot, otherwise the most greens wins.
      </p>

      {!loaded && <p className="game-hint">Loading…</p>}

      {loaded && gated && (
        <p className="game-hint" id="guessmate-gated">
          Guessmate is part of Pear Plus.
        </p>
      )}

      {loaded && !gated && loadError && (
        <>
          <p className="game-hint" role="alert">
            Could not reach Guessmate just now.
          </p>
          <div className="game-actions">
            <Pressable className="pear-btn pear-btn-blush" id="btn-guessmate-retry" onClick={() => void reload()}>
              Try again
            </Pressable>
          </div>
        </>
      )}

      {loaded && !gated && !loadError && !match && (
        <div id="guessmate-entry">
          <p className="game-hint">How do you want to play?</p>
          <div className="game-cats" role="group" aria-label="Match pace">
            <button
              type="button"
              id="gm-mode-async"
              aria-pressed={mode === 'async'}
              className={`game-cat${mode === 'async' ? ' is-on' : ''}`}
              onClick={() => setMode('async')}
            >
              Whenever we like
            </button>
            <button
              type="button"
              id="gm-mode-live"
              aria-pressed={mode === 'live'}
              className={`game-cat${mode === 'live' ? ' is-on' : ''}`}
              onClick={() => setMode('live')}
            >
              Live, two minutes a turn
            </button>
          </div>
          <div className="game-actions">
            <Pressable
              className="pear-btn pear-btn-mustard"
              id="btn-guessmate-start"
              disabled={busy}
              onClick={() => void start(mode)}
            >
              Start a duel
            </Pressable>
          </div>
        </div>
      )}

      {match && match.state === 'words' && (
        <div id="guessmate-words">
          {match.myWordSet ? (
            <>
              <p className="game-status" id="gm-word-locked">
                Your word is locked in. Waiting for {partnerName} to pick one.
              </p>
              {match.partnerWordSet && (
                <p className="game-hint">Both words are in — the first guess is next.</p>
              )}
            </>
          ) : (
            <form className="gm-form" onSubmit={(e) => void setWord(e)}>
              <label className="game-hint" htmlFor="gm-word-input">
                Your secret word — five letters, a real word.
              </label>
              <input
                id="gm-word-input"
                className="game-input gm-input"
                value={wordDraft}
                onChange={e => setWordDraft(e.target.value.replace(/[^a-zA-Z]/g, '').slice(0, 5))}
                maxLength={5}
                autoComplete="off"
                autoCapitalize="none"
                spellCheck={false}
                aria-describedby="guessmate-hint"
                disabled={busy}
              />
              <div className="game-actions">
                <Pressable
                  className="pear-btn pear-btn-mustard"
                  id="btn-gm-word"
                  disabled={busy || wordDraft.length !== 5}
                >
                  Lock in my word
                </Pressable>
              </div>
            </form>
          )}
        </div>
      )}

      {match && match.state !== 'words' && (
        <div id="guessmate-play">
          <p className="game-status" id="gm-status">
            {match.state === 'settled'
              ? outcomeLine(match)
              : match.myTurn
                ? clockLine != null
                  ? `Your turn — ${clockLine} left.`
                  : 'Your turn.'
                : `Waiting for ${partnerName}.`}
          </p>

          <p className="game-hint gm-board-label">Yours — {match.myScore} greens of {match.myGuesses.length} guesses</p>
          <Board guesses={match.myGuesses} label={`${myName}'s guesses`} />

          <p className="game-hint gm-board-label">
            {partnerName}'s — {match.partnerScore} greens of {match.partnerGuesses.length} guesses
          </p>
          <Board guesses={match.partnerGuesses} label={`${partnerName}'s guesses`} />

          {match.state === 'playing' && match.myTurn && (
            <form className="gm-form" onSubmit={(e) => void guess(e)}>
              <label className="game-hint" htmlFor="gm-guess-input">
                Your guess
              </label>
              <input
                id="gm-guess-input"
                className="game-input gm-input"
                value={guessDraft}
                onChange={e => setGuessDraft(e.target.value.replace(/[^a-zA-Z]/g, '').slice(0, 5))}
                maxLength={5}
                autoComplete="off"
                autoCapitalize="none"
                spellCheck={false}
                disabled={busy}
              />
              <div className="game-actions">
                <Pressable
                  className="pear-btn pear-btn-mustard"
                  id="btn-gm-guess"
                  disabled={busy || guessDraft.length !== 5}
                >
                  Guess
                </Pressable>
              </div>
            </form>
          )}

          {match.state === 'settled' && (
            <p className="game-hint" id="gm-reveal">
              {partnerName}'s word was {match.partnerWord?.toUpperCase() ?? '—'}; yours was{' '}
              {match.myWord?.toUpperCase() ?? '—'}.
            </p>
          )}
        </div>
      )}

      {error && (
        <p className="game-error" role="alert" id="guessmate-error">
          {error}
        </p>
      )}
    </section>
  );
}
